import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import { ScrollAreaHorizontal } from '@neo/primitives/scroll-area'
import { Expand } from 'lucide-react'
import { useState } from 'react'
import { CodeBlock } from 'v2/pages/contacts/data-capture-setup/components/code-block'
import type { NodeChunkCreatedEvent, NodeExecutedEvent } from '../types'
import { ToolCallHeader } from './header'
import { getToolCallValueMaps } from './utils'

export const JsonExpandableView = ({
  data,
  title,
  maxLength = 100,
}: {
  data: string | undefined
  title: string
  maxLength?: number
}) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!data) return <span className="text-muted-foreground">-</span>

  const truncatedData = data.length > maxLength ? `${data.substring(0, maxLength)}...` : data

  let prettyJson = data
  try {
    const parsed = JSON.parse(data)
    prettyJson = JSON.stringify(parsed, null, 2)
  } catch {
    prettyJson = data
  }

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 font-jetbrains text-xs-400 text-primary whitespace-break-spaces break-all overflow-hidden">
        {truncatedData}
      </div>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="iconXs"
            className="shrink-0 text-secondary"
            aria-label={`Expand ${title}`}
            data-testid="button-expand-json"
          >
            <Expand className="h-3 w-3" />
          </Button>
        </DialogTrigger>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <ScrollAreaHorizontal className="max-h-[60vh]">
            <div className="relative group [&_pre]:max-h-[60vh] [&_pre]:overflow-y-auto">
              <CodeBlock code={prettyJson} language="json" showCopyButton={false} />
              <ClipboardCopyButton
                textToCopy={prettyJson}
                tooltipText="Copy JSON"
                aria-label="Copy JSON"
                position="topRight"
                data-testid="button-copy-json"
              />
            </div>
          </ScrollAreaHorizontal>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const ToolCallAnalysis = ({
  node,
  chunk,
  hideNodeContext = false,
}: {
  node: NodeExecutedEvent
  chunk: NodeChunkCreatedEvent
  hideNodeContext?: boolean
}) => {
  const valueMaps = getToolCallValueMaps(node, chunk, false, hideNodeContext)

  return (
    <div className="flex flex-col gap-2">
      {valueMaps.map((valueMap, index) => (
        <div key={index} className="flex flex-col gap-2">
          <ToolCallHeader node={node} />
          <div className="flex flex-col gap-4 border-t border-border px-2 py-3">
            {Array.from(valueMap.entries()).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2">
                <div className="font-inter text-s-400 text-secondary w-28 shrink-0">{key}:</div>
                <div
                  className={cn(
                    'text-primary min-w-0 flex-1 whitespace-break-spaces break-words',
                    ['Executed tool node', 'Previous node', 'Chosen path'].includes(key)
                      ? 'font-inter text-s-400'
                      : 'font-jetbrains text-xs-400',
                  )}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
